import type { FilterBarVm, FilterControlVm } from '@ui-kit/filter-bar.component'
import type { ColumnSortVm } from '@ui-kit/table.component'
import type { ColumnFilterVm } from '@ui-kit/column-filter.component'
import type { PaginationVm } from '@ui-kit/pagination.component'
import type { I18nRepository } from '@core/infra/i18n/i18n.repository'
import type { FiltersConfig, FilterScope, ScopeDefinition } from './filters.config'
import type { ActiveFiltersPm, FiltersRepository } from './filters.repository'

/**
 * Config + selection → the bar's ViewModel. Shared because it is the same
 * translation for every list, and duplicating it per Presenter is how five
 * toolbars end up looking like five toolbars.
 *
 * What is deliberately *not* here: applying the selection. Which items a choice
 * keeps depends on the shape of the item, so each list's Presenter owns its own
 * predicates and this stays free of domain knowledge.
 */
export function buildFilterBarVm(
  scope: FilterScope,
  filtersConfig: FiltersConfig,
  filtersRepository: FiltersRepository,
  i18n: I18nRepository,
  counts: { shown: number; total: number },
  /**
   * Runtime options for each `'lookup'` filter, keyed by the filter's id — the
   * list Presenter passes what the workspace has created (task types,
   * locations…). A `'lookup'` filter with no entry here renders as an empty
   * dropdown, same as a `'choice'` whose config list is empty.
   */
  lookups: Readonly<Record<string, readonly { value: string; label: string }[]>> = {},
): FilterBarVm {
  const definition = filtersConfig.definitionFor(scope)
  const active = filtersRepository.activeFor(scope)
  const t = i18n.t.bind(i18n)

  // A column-bound filter (currently only 'number') draws its control on the
  // table header instead — see buildColumnFilter — so the bar does not offer
  // the same narrowing twice.
  const controls = definition.filters
    .filter((filter) => filter.column === undefined)
    .map((filter): FilterControlVm => {
      const value = active.values[filter.id] ?? ''

      if (filter.kind === 'text') {
        return {
          id: filter.id,
          kind: 'text',
          label: t(filter.labelKey),
          placeholder: t(filter.placeholderKey),
          value,
        }
      }

      if (filter.kind === 'choice') {
        return {
          id: filter.id,
          kind: 'choice',
          label: t(filter.labelKey),
          value,
          anyLabel: t('filters.any'),
          options: filter.options.map((option) => ({ value: option.id, label: t(option.labelKey) })),
        }
      }

      if (filter.kind === 'lookup') {
        return {
          id: filter.id,
          kind: 'choice',
          label: t(filter.labelKey),
          value,
          anyLabel: t('filters.any'),
          options: [...(lookups[filter.id] ?? [])],
        }
      }

      if (filter.kind === 'date') {
        const range = parseDateRange(value) ?? {}
        return {
          id: filter.id,
          kind: 'date',
          label: t(filter.labelKey),
          min: range.min ?? '',
          max: range.max ?? '',
          minLabel: t('filters.date.min'),
          maxLabel: t('filters.date.max'),
        }
      }

      return { id: filter.id, kind: 'toggle', label: t(filter.labelKey), on: value === 'on' }
    })

  const narrowed = filtersRepository.hasActiveFilters(scope)

  /**
   * One dismissible chip per active filter, which is the part a bar of controls
   * cannot do on its own: a dropdown two scroll-lines up does not answer "what am
   * I looking at right now", and a filter set on an earlier visit is invisible
   * until someone reopens every control to check.
   *
   * A chip carries the filter's own label so it reads without its control:
   * "Ownership: Mine" rather than "Mine".
   */
  const chips = definition.filters.flatMap((filter) => {
    const value = active.values[filter.id]
    if (value === undefined || value.length === 0) return []

    const label = t(filter.labelKey)
    if (filter.kind === 'text') return [{ id: filter.id, label: `${label}: ${value}` }]
    if (filter.kind === 'choice') {
      const option = filter.options.find((candidate) => candidate.id === value)
      // An unknown option id means config changed under a stored selection; the
      // raw id is more use than dropping the chip and hiding the narrowing.
      return [{ id: filter.id, label: `${label}: ${option ? t(option.labelKey) : value}` }]
    }
    if (filter.kind === 'lookup') {
      const option = (lookups[filter.id] ?? []).find((candidate) => candidate.value === value)
      // Same fallback as 'choice': an id whose row was deleted still names the
      // narrowing better than a dropped chip.
      return [{ id: filter.id, label: `${label}: ${option ? option.label : value}` }]
    }
    if (filter.kind === 'number') {
      const formatted = formatNumberChip(value, i18n)
      // Malformed input (e.g. a hand-edited URL) narrows nothing — see
      // matchesNumber — so it earns no chip either; there is nothing to name.
      return formatted === null ? [] : [{ id: filter.id, label: `${label}: ${formatted}` }]
    }
    if (filter.kind === 'date') {
      const formatted = formatDateChip(value, i18n)
      return formatted === null ? [] : [{ id: filter.id, label: `${label}: ${formatted}` }]
    }
    // A toggle's label is already a phrase ("Only mine"), so prefixing it reads
    // as a stutter.
    return [{ id: filter.id, label }]
  })

  // The chosen sort names its own two ends when "A→Z" would not describe them —
  // a date or a count has no alphabet.
  const sorts = definition.sorts ?? []
  const chosenSort = sorts.find((sort) => sort.id === active.sortId)
  const directionKey = active.ascending
    ? (chosenSort?.ascendingLabelKey ?? 'filters.ascending')
    : (chosenSort?.descendingLabelKey ?? 'filters.descending')

  return {
    controls,
    /**
     * A table's headers carry the sort, so the bar drops its dropdown rather
     * than offering a second control for the same axis.
     */
    showSort: definition.sortInHeaders !== true,
    sortLabel: t('filters.sortBy'),
    sortId: active.sortId,
    sortOptions: sorts.map((sort) => ({ value: sort.id, label: t(sort.labelKey) })),
    directionLabel: t(directionKey),
    ascending: active.ascending,
    showClear: narrowed,
    clearLabel: t('filters.clear'),
    chips,
    removeLabel: t('filters.remove'),
    /**
     * Always present, unlike the old summary which appeared only when the two
     * numbers differed. A count that vanishes is a count you cannot trust: its
     * absence had to be read as "nothing is filtered", which is exactly the
     * question it was there to answer.
     */
    countLabel: counts.shown === counts.total ? t('filters.totalCount', counts) : t('filters.summary', counts),
  }
}

/**
 * One column header's share of the sort.
 *
 * Here rather than in each table's Presenter for the same reason buildFilterBarVm
 * is: the mapping from "which sort is active" to "what this header shows and what
 * clicking it will do next" is identical for every table, and four copies of it
 * is four chances to describe the same cycle differently.
 *
 * The `nextLabel` is derived from the cycle in FiltersRepository.cycleSort and
 * has to keep agreeing with it — if that gains a step, this gains a phrase.
 */
export function buildColumnSort(
  scope: FilterScope,
  sortId: string,
  filtersConfig: FiltersConfig,
  filtersRepository: FiltersRepository,
  i18n: I18nRepository,
): ColumnSortVm {
  const active = filtersRepository.activeFor(scope)
  const definition = filtersConfig.definitionFor(scope)
  const start = (definition.sorts ?? []).find((sort) => sort.id === sortId)?.startAscending ?? true

  if (active.sortId !== sortId) {
    return { sortId, state: 'none', nextLabel: i18n.t('filters.sort.apply') }
  }
  return {
    sortId,
    state: active.ascending ? 'ascending' : 'descending',
    nextLabel: i18n.t(active.ascending === start ? 'filters.sort.reverse' : 'filters.sort.reset'),
  }
}

/**
 * The URL codec for one list's narrowing.
 *
 * Two small pure functions rather than a method on the Repository: the shape of
 * the state and the shape of the address bar are different concerns, and the
 * Repository has no business knowing that a URL exists.
 *
 * Only what the user chose is written. A list at its defaults produces an empty
 * query, so an untouched page keeps a clean URL and a shared link carries
 * exactly the narrowing that was on screen — no more.
 */
export function filtersToQuery(active: ActiveFiltersPm, definition: ScopeDefinition): Record<string, string> {
  const query: Record<string, string> = {}

  for (const filter of definition.filters) {
    const value = active.values[filter.id]
    if (value !== undefined && value.length > 0) query[filter.id] = value
  }

  if (active.page > 1) query.page = String(active.page)
  if (definition.defaultSortId !== undefined && active.sortId !== definition.defaultSortId) query.sort = active.sortId
  if (active.ascending !== (definition.defaultAscending ?? true)) query.dir = active.ascending ? 'asc' : 'desc'

  return query
}

/**
 * The reverse, tolerating anything: a URL is user input, so an unknown filter id
 * or a sort that no longer exists is dropped rather than trusted. Returning the
 * scope's own defaults for whatever is missing means a hand-edited URL narrows a
 * list wrongly at worst, never breaks it.
 */
export function queryToFilters(query: Readonly<Record<string, string>>, definition: ScopeDefinition): ActiveFiltersPm {
  const values: Record<string, string> = {}
  for (const filter of definition.filters) {
    const value = query[filter.id]
    if (value !== undefined && value.length > 0) values[filter.id] = value
  }

  const sortId = (definition.sorts ?? []).some((sort) => sort.id === query.sort)
    ? query.sort!
    : (definition.defaultSortId ?? '')
  const ascending = query.dir === 'asc' ? true : query.dir === 'desc' ? false : (definition.defaultAscending ?? true)

  const page = Number.parseInt(query.page ?? '', 10)

  return { values, sortId, ascending, page: Number.isFinite(page) && page > 0 ? page : 1 }
}

/**
 * One page of an already-filtered, already-sorted list, plus the wording for its
 * controls.
 *
 * Shared for the reason every other helper here is: seven lists slicing their
 * own arrays is seven chances to be off by one at the boundary, and the clamp
 * below is the part that is easy to forget entirely.
 */
export function paginate<T>(
  scope: FilterScope,
  rows: readonly T[],
  filtersConfig: FiltersConfig,
  filtersRepository: FiltersRepository,
  i18n: I18nRepository,
): { rows: T[]; pagination: PaginationVm } {
  const definition = filtersConfig.definitionFor(scope)
  if (definition.pageSize === undefined) {
    throw new Error(`paginate() called for scope '${scope}', which declares no pageSize (see ScopeDefinition).`)
  }
  const pageSize = definition.pageSize
  const active = filtersRepository.activeFor(scope)

  // Clamped rather than trusted: a page number can arrive from a URL, and rows
  // disappear under a stored one when someone else deletes something. An
  // out-of-range page shows the last page, never a blank screen.
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize))
  const page = Math.min(active.page, pageCount)
  const from = (page - 1) * pageSize

  return {
    rows: rows.slice(from, from + pageSize),
    pagination: {
      show: rows.length > pageSize,
      rangeLabel: i18n.t('pagination.range', {
        from: rows.length === 0 ? 0 : from + 1,
        to: Math.min(from + pageSize, rows.length),
        total: rows.length,
      }),
      previousLabel: i18n.t('pagination.previous'),
      nextLabel: i18n.t('pagination.next'),
      canPrevious: page > 1,
      canNext: page < pageCount,
    },
  }
}

/**
 * The stored encoding for a number filter: `"min.."`, `"..max"`, or
 * `"min..max"`. Parsed leniently — anything else (including empty) has no
 * bound, which is what an untouched or hand-edited value should mean.
 */
function parseNumberRange(raw: string): { min?: number; max?: number } | null {
  const match = raw.trim().match(/^(-?\d+(?:\.\d+)?)?\.\.(-?\d+(?:\.\d+)?)?$/)
  if (!match) return null

  const [, minText, maxText] = match
  if (minText === undefined && maxText === undefined) return null
  return {
    min: minText === undefined ? undefined : Number(minText),
    max: maxText === undefined ? undefined : Number(maxText),
  }
}

/** "12+", "up to 12", or "3–12" — whichever bound(s) are actually set. */
function formatNumberChip(raw: string, i18n: I18nRepository): string | null {
  const range = parseNumberRange(raw)
  if (!range) return null

  if (range.min !== undefined && range.max !== undefined) {
    return i18n.t('filters.number.betweenChip', { min: range.min, max: range.max })
  }
  if (range.min !== undefined) return i18n.t('filters.number.atLeastChip', { min: range.min })
  return i18n.t('filters.number.atMostChip', { max: range.max as number })
}

/**
 * The stored encoding for a date filter — same shape as `parseNumberRange`,
 * bounds being ISO `YYYY-MM-DD`. Parsed leniently: anything else (including
 * empty) has no bound.
 */
function parseDateRange(raw: string): { min?: string; max?: string } | null {
  const match = raw.trim().match(/^(\d{4}-\d{2}-\d{2})?\.\.(\d{4}-\d{2}-\d{2})?$/)
  if (!match) return null

  const [, min, max] = match
  if (min === undefined && max === undefined) return null
  return { min, max }
}

/** The exact inverse of `parseDateRange`. Both blank means "no filter". */
export function encodeDateRange(min: string, max: string): string {
  if (min.trim().length === 0 && max.trim().length === 0) return ''
  return `${min.trim()}..${max.trim()}`
}

/** "24 Aug", "up to 24 Aug", "from 24 Aug", or "24 Aug–1 Sep" — ISO dates, unformatted (no FormatGateway here). */
function formatDateChip(raw: string, i18n: I18nRepository): string | null {
  const range = parseDateRange(raw)
  if (!range) return null

  if (range.min !== undefined && range.max !== undefined) {
    if (range.min === range.max) return i18n.t('filters.date.onChip', { date: range.min })
    return i18n.t('filters.date.betweenChip', { min: range.min, max: range.max })
  }
  if (range.min !== undefined) return i18n.t('filters.date.afterChip', { date: range.min })
  return i18n.t('filters.date.beforeChip', { date: range.max as string })
}

/**
 * A date column's predicate — the sibling of `matchesNumber`. Malformed or
 * absent input narrows nothing. A `null` value (no due date) fails a set
 * filter, same as it fails the `overdue` toggle.
 */
export function matchesDate(active: ActiveFiltersPm, filterId: string, value: string | null): boolean {
  const raw = active.values[filterId] ?? ''
  if (raw.length === 0) return true

  const range = parseDateRange(raw)
  if (!range) return true
  if (value === null) return false

  if (range.min !== undefined && value < range.min) return false
  if (range.max !== undefined && value > range.max) return false
  return true
}

/**
 * A funnel's control for a column-bound number filter — the header-level twin
 * of `buildColumnSort`. Returns `{ kind: 'none' }` (CI-9's out-rule) when this
 * scope declares no such filter for `filterId` — never `undefined` — so a
 * `<TH>` renders no funnel and every VM that carries a `ColumnFilterVm` means
 * exactly that type, not `ColumnFilterVm | undefined`.
 */
export function buildColumnFilter(
  scope: FilterScope,
  filterId: string,
  filtersConfig: FiltersConfig,
  filtersRepository: FiltersRepository,
  i18n: I18nRepository,
): ColumnFilterVm {
  const definition = filtersConfig.definitionFor(scope)
  const filter = definition.filters.find((candidate) => candidate.id === filterId)
  // Column-bound only: an un-bound 'text' filter is the bar's own free-text
  // search, not a funnel — same distinction buildFilterBarVm's controls draw.
  if (!filter || filter.column === undefined || (filter.kind !== 'number' && filter.kind !== 'text')) {
    return { kind: 'none' }
  }

  const active = filtersRepository.activeFor(scope)
  const raw = active.values[filterId] ?? ''

  if (filter.kind === 'text') {
    return {
      kind: 'text',
      filterId,
      label: i18n.t(filter.labelKey),
      active: raw.length > 0,
      value: raw,
      placeholder: i18n.t(filter.placeholderKey),
      applyLabel: i18n.t('filters.column.apply'),
      clearLabel: i18n.t('filters.clear'),
    }
  }

  const range = parseNumberRange(raw)

  return {
    kind: 'number',
    filterId,
    label: i18n.t(filter.labelKey),
    active: raw.length > 0,
    min: range?.min?.toString() ?? '',
    max: range?.max?.toString() ?? '',
    minLabel: i18n.t('filters.number.min'),
    maxLabel: i18n.t('filters.number.max'),
    applyLabel: i18n.t('filters.column.apply'),
    clearLabel: i18n.t('filters.clear'),
    // Same wording the filter bar's own chip uses (formatNumberChip) — the
    // component used to hardcode its own "–"/"…" summary, untranslated.
    summary: formatNumberChip(raw, i18n) ?? '',
  }
}

/**
 * Encodes a min/max pair typed into a funnel back into the stored string —
 * the exact inverse of `parseNumberRange`. Both blank means "no filter",
 * which `FiltersRepository.setValue` already reads from an empty string.
 */
export function encodeNumberRange(min: string, max: string): string {
  if (min.trim().length === 0 && max.trim().length === 0) return ''
  return `${min.trim()}..${max.trim()}`
}

/**
 * A numeric column's predicate — the sibling of `matchesText`. Malformed or
 * absent input narrows nothing, same policy as `queryToFilters`: a broken
 * filter should never be mistaken for "excludes everything".
 */
export function matchesNumber(active: ActiveFiltersPm, filterId: string, value: number): boolean {
  const raw = active.values[filterId] ?? ''
  if (raw.length === 0) return true

  const range = parseNumberRange(raw)
  if (!range) return true

  if (range.min !== undefined && value < range.min) return false
  if (range.max !== undefined && value > range.max) return false
  return true
}

/** Case-insensitive "does any of these fields contain the typed text". */
export function matchesText(active: ActiveFiltersPm, filterId: string, ...fields: string[]): boolean {
  const needle = (active.values[filterId] ?? '').trim().toLowerCase()
  if (needle.length === 0) return true
  return fields.some((field) => field.toLowerCase().includes(needle))
}

/** Compare for sorting, honouring the chosen direction. */
export function compareBy(active: ActiveFiltersPm, left: string, right: string): number {
  const result = left.localeCompare(right)
  return active.ascending ? result : -result
}

/**
 * The same, for counts and other numbers. Separate from `compareBy` rather than
 * stringifying: `'10'.localeCompare('9')` is negative, so a count sorted as text
 * puts ten before nine.
 */
export function compareNumbers(active: ActiveFiltersPm, left: number, right: number): number {
  const result = left - right
  return active.ascending ? result : -result
}
